import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import type {
  TypeCibleEtiquette,
  TypeCaisse,
  EtatCaisse,
  DimensionsCaisse,
  Dimensions,
  creationEmplacementSchema,
  modificationEmplacementSchema,
  creationCaisseSchema,
  modificationCaisseSchema,
  deplacementsInventaireSchema,
  perimetreInventaireSchema,
  scansInventaireSchema,
  resolutionInventaireSchema,
  pdfEtiquettesSchema,
} from '@artosera/shared';
import { api, requeteApi, requeteTelechargement, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { useMutation } from './mutation.js';
import type { Pagination } from './types.js';
import type { ReponseDocumentSigne } from './documentsGalerie.js';

export interface CibleEtiquette {
  type: TypeCibleEtiquette;
  id: string;
}
export interface EmplacementPresente {
  id: string;
  lieuId: string;
  parentId: string | null;
  nom: string;
  code: string;
  actif: boolean;
  notes: string;
  chemin: string;
  creeLe: string;
  modifieLe: string;
}
export interface NoeudEmplacement extends EmplacementPresente {
  enfants: NoeudEmplacement[];
}
export interface ContenuCaisse {
  cle: string;
  oeuvreId: string;
  exemplaireId: string | null;
  ajouteLe: string;
  ajoutePar: string | null;
}
export interface CaissePresentee {
  id: string;
  numero: string;
  type: TypeCaisse;
  etat: EtatCaisse;
  dimensionsInterieures: DimensionsCaisse | null;
  dimensionsExterieures: DimensionsCaisse | null;
  lieuId: string | null;
  emplacementId: string | null;
  mediaIds: string[];
  notes: string;
  revision: number;
  contenu: ContenuCaisse[];
  creeLe: string;
  modifieLe: string;
}
export interface FicheCaisse extends CaissePresentee {
  contenu: (ContenuCaisse & {
    titre: string;
    artisteNom: string;
    numeroInventaire: string;
    numeroExemplaire: string | null;
    dimensions: Dimensions | null;
    nentrePas: boolean;
  })[];
}
export interface EtiquettePresentee {
  id: string;
  jeton: string;
  cible: CibleEtiquette;
  active: boolean;
  creeLe: string;
  remplaceeLe: string | null;
}
export interface EtatCartel {
  oeuvreId: string;
  /** Visible sur le site, ni archivée ni retirée : sa fiche publique s'ouvre. */
  publiable: boolean;
  actif: boolean;
  afficherPrix: boolean;
  adresse: string | null;
}
export interface ResolutionEtiquette {
  cible: CibleEtiquette;
  resume:
    | {
        titre: string;
        artisteNom: string;
        numeroInventaire: string;
        numeroExemplaire?: string;
        introuvable: boolean;
        oeuvreId: string;
        lieuActuel: string;
      }
    | { code: string; chemin: string }
    | { numero: string; etat: EtatCaisse };
}
export type PerimetreInventaire = z.infer<typeof perimetreInventaireSchema>;
export type ScanAEnvoyer = z.infer<typeof scansInventaireSchema>['scans'][number];
export type DeplacementInventaire = z.infer<typeof deplacementsInventaireSchema>;
export interface CampagnePresentee {
  id: string;
  numero: string;
  perimetre: PerimetreInventaire;
  etat: 'ouverte' | 'close';
  revision: number;
  ouverteLe: string;
  ouvertePar: string | null;
  closeLe: string | null;
  closePar: string | null;
  rapportDocumentId: string | null;
}
export interface ScanPresente extends ScanAEnvoyer {
  id: string;
  cible: CibleEtiquette | null;
  recuLe: string;
  auteurId: string | null;
  appareil: string;
}
export interface ResolutionInventaire {
  cle: string;
  geste: 'deplacer_ici' | 'introuvable' | 'ignorer';
  note: string;
  par: string | null;
  le: string;
}
export interface LigneInventaire {
  cle: string;
  cible: CibleEtiquette;
  titre: string;
  numero: string;
  lieuAttendu: string;
  resolution: ResolutionInventaire | null;
}
export interface ResultatInventaire {
  attendues: LigneInventaire[];
  presentes: LigneInventaire[];
  absentes: LigneInventaire[];
  inattendues: LigneInventaire[];
  inconnus: ScanPresente[];
  scans: ScanPresente[];
  resolutions: ResolutionInventaire[];
  scanneurs: { auteurId: string | null; nombre: number }[];
}
export interface Rangement {
  emplacement: EmplacementPresente | null;
  caisse: CaissePresentee | null;
  etiquette: { id: string; jeton: string } | null;
  introuvable: { id: string; note: string; declareeLe: string } | null;
}
export interface IntrouvablePresentee {
  id: string;
  oeuvreId: string;
  exemplaireId: string | null;
  campagneId: string;
  note: string;
  declareeLe: string;
  declareePar: string | null;
  retrouveeLe: string | null;
  retrouveePar: string | null;
}
export interface FiltresCaisses {
  q?: string;
  type?: TypeCaisse;
  etat?: EtatCaisse;
  lieuId?: string;
  emplacementId?: string;
  typeLieu?: string;
  dimensionMinCm?: number;
  page?: number;
  parPage?: number;
}
const chemin = (g: string, route: string) => `/g/${encodeURIComponent(g)}/${route}`;
const idRoute = (id: string) => encodeURIComponent(id);
function parametres(filtres: object) {
  return new URLSearchParams(
    Object.entries(filtres)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => [k, String(v)]),
  ).toString();
}
export const reserveApi = {
  emplacements: (g: string, inclureInactifs = false, appel?: AppelApi) =>
    api.get<{ emplacements: NoeudEmplacement[] }>(
      chemin(g, `emplacements?inclureInactifs=${inclureInactifs}`),
      { galerie: g, ...appel },
    ),
  emplacement: (g: string, id: string, appel?: AppelApi) =>
    api.get<{
      emplacement: EmplacementPresente & {
        enfants: EmplacementPresente[];
        nombreOeuvres: number;
        nombreCaisses: number;
      };
    }>(chemin(g, `emplacements/${idRoute(id)}`), { galerie: g, ...appel }),
  creerEmplacement: (
    g: string,
    corps: z.infer<typeof creationEmplacementSchema>,
    appel?: AppelApi,
  ) =>
    api.post<{ emplacement: EmplacementPresente }>(chemin(g, 'emplacements'), corps, {
      galerie: g,
      ...appel,
    }),
  modifierEmplacement: (
    g: string,
    id: string,
    corps: z.infer<typeof modificationEmplacementSchema>,
    appel?: AppelApi,
  ) =>
    api.patch<{ emplacement: EmplacementPresente }>(
      chemin(g, `emplacements/${idRoute(id)}`),
      corps,
      { galerie: g, ...appel },
    ),
  caisses: (g: string, filtres: FiltresCaisses = {}, appel?: AppelApi) =>
    api.get<{ caisses: CaissePresentee[]; pagination: Pagination }>(
      chemin(g, `caisses?${parametres(filtres)}`),
      { galerie: g, ...appel },
    ),
  caisse: (g: string, id: string, appel?: AppelApi) =>
    api.get<{ caisse: FicheCaisse }>(chemin(g, `caisses/${idRoute(id)}`), { galerie: g, ...appel }),
  creerCaisse: (g: string, corps: z.infer<typeof creationCaisseSchema>, appel?: AppelApi) =>
    api.post<{ caisse: CaissePresentee }>(chemin(g, 'caisses'), corps, { galerie: g, ...appel }),
  modifierCaisse: (
    g: string,
    id: string,
    corps: z.infer<typeof modificationCaisseSchema>,
    appel?: AppelApi,
  ) =>
    api.patch<{ caisse: CaissePresentee }>(chemin(g, `caisses/${idRoute(id)}`), corps, {
      galerie: g,
      ...appel,
    }),
  ajouterContenu: (
    g: string,
    id: string,
    corps: { oeuvreId: string; exemplaireId?: string | null; revision: number },
    appel?: AppelApi,
  ) =>
    api.post<{ caisse: FicheCaisse }>(chemin(g, `caisses/${idRoute(id)}/contenu`), corps, {
      galerie: g,
      ...appel,
    }),
  retirerContenu: (g: string, id: string, cle: string, revision: number, appel?: AppelApi) =>
    requeteApi<{ caisse: FicheCaisse }>(
      chemin(g, `caisses/${idRoute(id)}/contenu/${idRoute(cle)}`),
      { method: 'DELETE', corps: { revision }, galerie: g, ...appel },
    ),
  deplacerCaisse: (
    g: string,
    id: string,
    corps: { lieuId: string | null; emplacementId?: string | null; revision: number },
    appel?: AppelApi,
  ) =>
    api.post<{ caisse: FicheCaisse }>(chemin(g, `caisses/${idRoute(id)}/deplacer`), corps, {
      galerie: g,
      ...appel,
    }),
  caissesMouvement: (g: string, id: string, caisseIds: string[], appel?: AppelApi) =>
    api.put<{ caisseIds: string[] }>(
      chemin(g, `caisses/mouvements/${idRoute(id)}`),
      { caisseIds },
      { galerie: g, ...appel },
    ),
  resoudre: (g: string, code: string, appel?: AppelApi) =>
    api.post<ResolutionEtiquette>(
      chemin(g, 'etiquettes/resoudre'),
      { code },
      { galerie: g, ...appel },
    ),
  etiquettes: (g: string, cibles: CibleEtiquette[], appel?: AppelApi) =>
    api.post<{ etiquettes: EtiquettePresentee[] }>(
      chemin(g, 'etiquettes'),
      { cibles },
      { galerie: g, ...appel },
    ),
  regenerer: (g: string, cible: CibleEtiquette, appel?: AppelApi) =>
    api.post<{ etiquette: EtiquettePresentee }>(
      chemin(g, 'etiquettes/regenerer'),
      { cible },
      { galerie: g, ...appel },
    ),
  pdfEtiquettes: (
    g: string,
    corps: z.infer<typeof pdfEtiquettesSchema>,
    langue: string,
    appel?: AppelApi,
  ) =>
    requeteTelechargement(chemin(g, `etiquettes/pdf?langue=${encodeURIComponent(langue)}`), {
      method: 'POST',
      corps,
      galerie: g,
      ...appel,
    }),
  etatCartels: (g: string, oeuvreIds: string[], appel?: AppelApi) =>
    api.post<{ cartels: EtatCartel[] }>(
      chemin(g, 'cartels/etat'),
      { oeuvreIds },
      { galerie: g, ...appel },
    ),
  revoquerCartel: (g: string, oeuvreId: string, appel?: AppelApi) =>
    api.post<{ revoque: boolean }>(
      chemin(g, 'cartels/revoquer'),
      { oeuvreId },
      { galerie: g, ...appel },
    ),
  deplacer: (g: string, corps: DeplacementInventaire, appel?: AppelApi) =>
    api.post<Pick<DeplacementInventaire, 'destination' | 'cibles'>>(
      chemin(g, 'inventaires/deplacements'),
      corps,
      { galerie: g, ...appel },
    ),
  rangement: (g: string, id: string, appel?: AppelApi) =>
    api.get<{ rangement: Rangement }>(chemin(g, `inventaires/oeuvres/${idRoute(id)}/rangement`), {
      galerie: g,
      ...appel,
    }),
  campagnes: (g: string, appel?: AppelApi) =>
    api.get<{ campagnes: CampagnePresentee[] }>(chemin(g, 'inventaires'), { galerie: g, ...appel }),
  ouvrirCampagne: (g: string, perimetre: PerimetreInventaire, appel?: AppelApi) =>
    api.post<{ campagne: CampagnePresentee }>(
      chemin(g, 'inventaires'),
      { perimetre },
      { galerie: g, ...appel },
    ),
  campagne: (g: string, id: string, appel?: AppelApi) =>
    api.get<{ campagne: CampagnePresentee; resultat: ResultatInventaire }>(
      chemin(g, `inventaires/${idRoute(id)}`),
      { galerie: g, ...appel },
    ),
  scans: (g: string, id: string, scans: ScanAEnvoyer[], appel?: AppelApi) =>
    api.post<{ scans: (ScanPresente & { rejoue: boolean })[] }>(
      chemin(g, `inventaires/${idRoute(id)}/scans`),
      { scans },
      { galerie: g, ...appel },
    ),
  resoudreEcart: (
    g: string,
    id: string,
    corps: z.infer<typeof resolutionInventaireSchema>,
    appel?: AppelApi,
  ) =>
    api.post<{ campagne: CampagnePresentee }>(
      chemin(g, `inventaires/${idRoute(id)}/resolutions`),
      corps,
      { galerie: g, ...appel },
    ),
  clore: (g: string, id: string, revision: number, appel?: AppelApi) =>
    api.post<{ campagne: CampagnePresentee }>(
      chemin(g, `inventaires/${idRoute(id)}/cloture`),
      { revision },
      { galerie: g, ...appel },
    ),
  rapport: (g: string, id: string, appel?: AppelApi) =>
    api.get<ReponseDocumentSigne>(chemin(g, `inventaires/${idRoute(id)}/rapport`), {
      galerie: g,
      ...appel,
    }),
  introuvables: (g: string, appel?: AppelApi) =>
    api.get<{ introuvables: IntrouvablePresentee[] }>(chemin(g, 'inventaires/introuvables'), {
      galerie: g,
      ...appel,
    }),
  retrouvee: (g: string, id: string, appel?: AppelApi) =>
    api.post<{ introuvable: IntrouvablePresentee }>(
      chemin(g, `inventaires/introuvables/${idRoute(id)}/retrouvee`),
      {},
      { galerie: g, ...appel },
    ),
};
export function aplatirEmplacements(noeuds: NoeudEmplacement[]): EmplacementPresente[] {
  return noeuds.flatMap((n) => [n, ...aplatirEmplacements(n.enfants)]);
}
export function useEmplacements(g: string, inclureInactifs = false) {
  return useQuery({
    queryKey: cleMetier(g, 'reserve', 'emplacements', inclureInactifs),
    queryFn: (c) => reserveApi.emplacements(g, inclureInactifs, appelDeRequete(c)),
  });
}
export function useCaisses(g: string, filtres: FiltresCaisses = {}) {
  return useQuery({
    queryKey: cleMetier(g, 'reserve', 'caisses', filtres),
    queryFn: (c) => reserveApi.caisses(g, filtres, appelDeRequete(c)),
  });
}
export function useCampagnes(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'reserve', 'campagnes'),
    queryFn: (c) => reserveApi.campagnes(g, appelDeRequete(c)),
  });
}
export function useCampagne(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'reserve', 'campagne', id),
    queryFn: (c) => reserveApi.campagne(g, id, appelDeRequete(c)),
  });
}
export function useRangement(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'reserve', 'rangement', id),
    queryFn: (c) => reserveApi.rangement(g, id, appelDeRequete(c)),
    select: (r) => r.rangement,
  });
}
export function useMutationReserve<T, R>(
  g: string,
  executer: (corps: T, appel: AppelApi) => Promise<R>,
) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee<R, Error, T, unknown>({
      networkMode: 'always',
      mutationFn: executer,
      onSuccess: () => {
        for (const domaine of ['reserve', 'oeuvres', 'mouvements', 'tableau-de-bord'])
          void client.invalidateQueries({ queryKey: cleMetier(g, domaine) });
      },
    }),
  );
}
